/**
 * O que faz: exibe um retorno programado acionável pela equipe humana com dados do lead e ações principais.
 * Onde usa: ReturnsBoard na página /retornos.
 * Conexões: LeadReturn, callbacks de navegação/reagendamento/conclusão e componentes shadcn.
 * Tabelas: Nenhuma; recebe dados já carregados de lead_returns pelos hooks da feature.
 * Data: 24/08/2026
 */

import { CalendarClock, Check, MessageSquare, RotateCcw, XCircle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import type { LeadReturn } from './returnTypes';

interface ReturnTaskCardProps {
  item: LeadReturn;
  onOpenConversation: (item: LeadReturn) => void;
  onComplete: (item: LeadReturn) => void;
  onReschedule: (item: LeadReturn) => void;
  onCancel: (item: LeadReturn) => void;
  isMutating?: boolean;
}

const statusLabelByValue: Record<LeadReturn['status'], string> = {
  pending: 'Pendente',
  claimed: 'Reservado pela IA',
  awaiting_approval: 'Aguardando aprovação',
  completed: 'Concluído',
  cancelled: 'Cancelado',
  failed: 'Falhou',
};

const executorLabelByValue: Record<LeadReturn['executor_type'], string> = {
  human: 'Humano',
  internal_ai: 'IA do sistema',
  external_ai: 'IA externa',
};

const executionModeLabelByValue: Record<LeadReturn['execution_mode'], string> = {
  draft_only: 'Rascunho',
  automatic: 'Automático',
};

const statusClassNameByValue: Record<LeadReturn['status'], string> = {
  pending: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  claimed: 'border-blue-200 bg-blue-50 text-blue-700',
  awaiting_approval: 'border-amber-200 bg-amber-50 text-amber-700',
  completed: 'border-slate-200 bg-slate-50 text-slate-600',
  cancelled: 'border-slate-200 bg-slate-50 text-slate-500',
  failed: 'border-red-200 bg-red-50 text-red-700',
};

const formatTimeInSaoPaulo = (dateTime: string) => {
  // Motivo: a equipe trabalha pelo horário da clínica; o banco salva UTC.
  return new Intl.DateTimeFormat('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).format(new Date(dateTime));
};

const formatDateInSaoPaulo = (dateTime: string) => {
  // Motivo: cards em tabs diferentes ainda precisam mostrar a data para evitar ambiguidade operacional.
  return new Intl.DateTimeFormat('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(new Date(dateTime));
};

export function ReturnTaskCard({
  item,
  onOpenConversation,
  onComplete,
  onReschedule,
  onCancel,
  isMutating = false,
}: ReturnTaskCardProps) {
  const isClaimedByWorker = item.status === 'claimed';
  const isActionDisabled = isMutating || isClaimedByWorker;
  // Motivo: a máquina de estados só permite failed -> pending; esconder ações inválidas evita erro operacional enganoso.
  const canComplete = item.status === 'pending' || item.status === 'awaiting_approval';
  const canCancel = item.status === 'pending' || item.status === 'awaiting_approval';
  const canReschedule = item.status !== 'completed' && item.status !== 'cancelled' && !isClaimedByWorker;

  return (
    <Card className="rounded-2xl border-gray-100 bg-white shadow-sm transition-shadow hover:shadow-md">
      <CardContent className="p-4 sm:p-5">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="min-w-0 flex-1 space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="outline" className={cn('rounded-full px-2.5 py-1 text-[11px] font-semibold', statusClassNameByValue[item.status])}>
                {statusLabelByValue[item.status]}
              </Badge>
              <Badge variant="outline" className="rounded-full border-gray-200 px-2.5 py-1 text-[11px] text-gray-600">
                {executorLabelByValue[item.executor_type]} · {executionModeLabelByValue[item.execution_mode]}
              </Badge>
            </div>

            <div>
              <h3 className="truncate text-base font-semibold text-gray-950">{item.lead_name}</h3>
              <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-500">
                <span className="inline-flex items-center gap-1.5">
                  <CalendarClock className="h-3.5 w-3.5" aria-hidden="true" />
                  {formatDateInSaoPaulo(item.scheduled_for)} às {formatTimeInSaoPaulo(item.scheduled_for)}
                </span>
                <span>Responsável: {item.responsible_name || 'Sem responsável'}</span>
              </div>
            </div>

            <p className="text-sm leading-relaxed text-gray-700">{item.reason}</p>

            {item.context_note ? (
              <p className="rounded-xl border border-gray-100 bg-gray-50 px-3 py-2 text-sm leading-relaxed text-gray-600">
                {item.context_note}
              </p>
            ) : null}

            {isClaimedByWorker ? (
              <p className="rounded-xl border border-blue-100 bg-blue-50 px-3 py-2 text-sm text-blue-800">
                Este retorno está reservado pela IA. Ele continua visível, mas as ações humanas ficam bloqueadas até a reserva terminar.
              </p>
            ) : null}
          </div>

          <div className="grid shrink-0 grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:justify-end lg:max-w-[360px]">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenConversation(item)}
              className="h-10 rounded-xl border-gray-200 text-gray-700 hover:bg-gray-50"
              aria-label={`Abrir conversa com ${item.lead_name}`}
            >
              <MessageSquare className="mr-2 h-4 w-4" aria-hidden="true" />
              Abrir conversa
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => onComplete(item)}
              disabled={isActionDisabled || !canComplete}
              className="h-10 rounded-xl border-emerald-200 text-emerald-700 hover:bg-emerald-50 disabled:hover:bg-transparent"
              aria-label={`Concluir retorno de ${item.lead_name}`}
            >
              <Check className="mr-2 h-4 w-4" aria-hidden="true" />
              Concluir
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => onReschedule(item)}
              disabled={isActionDisabled || !canReschedule}
              className="h-10 rounded-xl border-gray-200 text-gray-700 hover:bg-gray-50 disabled:hover:bg-transparent"
              aria-label={`Reagendar retorno de ${item.lead_name}`}
            >
              <RotateCcw className="mr-2 h-4 w-4" aria-hidden="true" />
              Reagendar
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => onCancel(item)}
              disabled={isActionDisabled || !canCancel}
              className="h-10 rounded-xl border-red-200 text-red-700 hover:bg-red-50 disabled:hover:bg-transparent"
              aria-label={`Cancelar retorno de ${item.lead_name}`}
            >
              <XCircle className="mr-2 h-4 w-4" aria-hidden="true" />
              Cancelar
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
